import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Share2,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { api } from '../../../context/AuthContext';
import { toast } from 'sonner';

const PLATFORMS = [
  {
    id: 'facebook',
    name: 'Facebook & Instagram',
    description: 'Connect Facebook Pages and Instagram Business accounts for post scheduling and profile management',
    icon: '📘',
    createUrl: 'https://www.facebook.com/pages/create',
  },
  {
    id: 'twitter',
    name: 'Twitter / X',
    description: 'Publish tweets and manage your Twitter presence',
    icon: '🐦',
    createUrl: 'https://twitter.com/i/flow/signup',
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    description: 'Share updates and manage your LinkedIn company page',
    icon: '💼',
    createUrl: 'https://www.linkedin.com/company/setup/new/',
  },
  {
    id: 'pinterest',
    name: 'Pinterest',
    description: 'Create pins and manage boards for visual content distribution',
    icon: '📌',
    createUrl: 'https://www.pinterest.com/business/create/',
  },
];

export default function SocialAccountsStep({ onNext, onBack }) {
  const [connections, setConnections] = useState({});
  const [connecting, setConnecting] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConnections();

    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    if (connected) {
      toast.success(`${connected.replace('_', ' ')} connected successfully!`);
      window.history.replaceState({}, '', window.location.pathname);
      fetchConnections();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchConnections = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/integrations/oauth/connections');
      setConnections(data || {});
    } catch {
      // show as not connected
    } finally {
      setLoading(false);
    }
  };

  const isConnected = (provider) => connections[provider]?.is_active === true;

  const handleConnect = async (provider) => {
    setConnecting(provider);
    try {
      const { data } = await api.get(`/integrations/oauth/${provider}/authorize`);
      if (data?.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || `Could not start ${provider} connection`;
      toast.error(detail);
      setConnecting(null);
    }
  };

  const anyConnected = PLATFORMS.some((p) => isConnected(p.id));

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
          <Share2 className="w-8 h-8 text-indigo-600" />
        </div>
        <h1 className="text-2xl font-bold font-heading mb-2">Connect Social Accounts</h1>
        <p className="text-slate-600 max-w-lg mx-auto">
          Link your social profiles so GDS can schedule posts, track engagement, and automate SMO activities across all platforms.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      ) : (
        <div className="space-y-4">
          {PLATFORMS.map((platform, index) => {
            const connected = isConnected(platform.id);
            return (
              <motion.div
                key={platform.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`gds-card transition-all ${connected ? 'border-emerald-200 bg-emerald-50/30' : ''}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-2xl">
                          {platform.icon}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{platform.name}</h3>
                            {connected ? (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Connected
                              </Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-700">Not Connected</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{platform.description}</p>
                          {connected && connections[platform.id]?.connected_email && (
                            <p className="text-xs text-slate-500 mt-1">
                              Connected as: {connections[platform.id].connected_email}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(platform.createUrl, '_blank')}
                          title="Create account"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          className={connected ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'gds-btn-primary'}
                          onClick={() => !connected && handleConnect(platform.id)}
                          disabled={connecting === platform.id || connected}
                        >
                          {connecting === platform.id ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</>
                          ) : connected ? (
                            <><CheckCircle2 className="w-4 h-4 mr-2" /> Connected</>
                          ) : (
                            'Connect'
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Help Card */}
      <Card className="gds-card bg-blue-50/50 border-blue-200">
        <CardContent className="p-6">
          <h4 className="font-medium mb-2">Don't have these accounts yet?</h4>
          <p className="text-sm text-slate-600 mb-4">
            You can create them now using the links above, or skip and connect later from your Integrations settings.
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-center">
        <Button variant="ghost" size="sm" onClick={fetchConnections} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh Status
        </Button>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button onClick={onNext} className="gds-btn-primary">
          {anyConnected ? 'Continue' : 'Skip for Now'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
